import { BillStatus, BillType, IBill, ITransactionLog, TransactionStatus, Vendors } from 'realhaus-sdk';

export enum PaymentMethod {
  INTERAC = 'INTERAC',
  EFT = 'EFT',
  CREDITCARD = 'CREDITCARD',
}

const BILL_TYPE_LABELS: Record<BillType, string> = {
  [BillType.RENT]: 'Rent',
  [BillType.SERVICE]: 'Service',
  [BillType.PET_FEE]: 'Pet fee',
  [BillType.NSF_FEE]: 'NSF fee',
  [BillType.OTHER_FEE]: 'Bill',
  [BillType.MOVE_IN_FEE]: 'Move-in fee',
  [BillType.MOVE_OUT_FEE]: 'Move-out fee',
  [BillType.PARKING_FEE]: 'Parking fee',
  [BillType.SMOKING_FEE]: 'Smoking fee',
  [BillType.KEY_DEPOSIT]: 'Key deposit',
  [BillType.SECURITY_DEPOSIT]: 'Security deposit',
};

/**
 * What a bill is for, in sentence case ("Rent", "Move-in fee"). A requested payment (OTHER_FEE)
 * is described by the note its requester wrote, when there is one.
 */
export const billTypeLabel = (type: BillType, note?: string) => {
  if (type === BillType.OTHER_FEE && note?.trim()) return note.trim();
  return BILL_TYPE_LABELS[type] ?? 'Bill';
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  [PaymentMethod.EFT]: 'Pay By Bank',
  [PaymentMethod.INTERAC]: 'Interac e-Transfer',
  [PaymentMethod.CREDITCARD]: 'Credit card',
};

export const paymentMethodLabel = (method?: string) =>
  method ? PAYMENT_METHOD_LABELS[method] ?? method : undefined;
/**
 * Calculate the net amount inclusive of the fee to deduct from the user for transferring a desired amount
 * calculating from the point of view that the vendor will charge Realhaus based on the net amount provided.
 * As a result this amount is charged to the user and is inclusive of that the fee the vendor will charge to Realhaus.
 * e.g. (amount + $0.65) + (1 - 2.9%)
 *
 * @param desiredAmount the desired amount to transfer
 * @param percentageFee the percentage fee the vendor will charge
 * @param fixedFee the fixed fee the vendor will charge
 * @param {number} minimumFee the minimum fee the vendor will charge
 * @param {number} maximumFee the maximum fee the vendor will charge
 * @return {number} amount to charge the user that will be inclusive of desired amount and vendor fee
 */
export const calculateNetAmountInclusively = (
  desiredAmount: number,
  percentageFee: number,
  fixedFee: number,
  minimumFee: number,
  maximumFee?: number,
) => {
  if (desiredAmount === 0) return 0;

  let total = (Number(desiredAmount) + Number(fixedFee)) / (1 - Number(percentageFee));
  if (total - Number(desiredAmount) < Number(minimumFee)) {
    total = Number(desiredAmount) + Number(minimumFee);
  }
  if (!!maximumFee && maximumFee > 0 && total - Number(desiredAmount) > Number(maximumFee)) {
    total = Number(desiredAmount) + Number(maximumFee);
  }

  return total;
};

export const getTrxFeeBreakdown = (amount: number, transactionFees: any) => {
  const interacFeeAmount = transactionFees?.INTERAC
    ? calculateNetAmountInclusively(
      amount,
      transactionFees?.INTERAC?.PERCENT,
      transactionFees?.INTERAC?.AMOUNT,
      transactionFees?.INTERAC?.MINIMUM ?? 0,
      transactionFees?.INTERAC?.MAXIMUM ?? 0,
    )
    : amount;
  const ccFeeAmount = transactionFees?.CREDITCARD
    ? calculateNetAmountInclusively(
      amount,
      transactionFees?.CREDITCARD?.PERCENT,
      transactionFees?.CREDITCARD?.AMOUNT,
      transactionFees?.CREDITCARD?.MINIMUM ?? 0,
      transactionFees?.CREDITCARD?.MAXIMUM ?? 0,
    )
    : amount;
  const eftFeeAmount = transactionFees?.EFT
    ? calculateNetAmountInclusively(
      amount,
      transactionFees?.EFT?.PERCENT,
      transactionFees?.EFT?.AMOUNT,
      transactionFees?.EFT?.MINIMUM ?? 0,
      transactionFees?.EFT?.MAXIMUM ?? 0,
    )
    : amount;

  return {
    [PaymentMethod.CREDITCARD]: {
      fee: Number((ccFeeAmount - amount).toFixed(2)),
      netAmount: Number(ccFeeAmount.toFixed(2)),
    },
    [PaymentMethod.EFT]: {
      fee: Number((eftFeeAmount - amount).toFixed(2)),
      netAmount: Number(eftFeeAmount.toFixed(2)),
    },
    [PaymentMethod.INTERAC]: {
      fee: Number((interacFeeAmount - amount).toFixed(2)),
      netAmount: Number(interacFeeAmount.toFixed(2)),
    },
  };
};

/**
 * Stripe checkout sessions abandoned without payment leave the transaction log PENDING.
 * Treat such logs as stale after 1 hour so the tenant can retry.
 */
const STALE_CHECKOUT_MS = 60 * 60 * 1000;

const IN_PROGRESS_TRX_STATUSES = [
  TransactionStatus.PENDING,
  TransactionStatus.PROCESSING,
  TransactionStatus.PENDING_REALHAUS,
  TransactionStatus.PENDING_VENDOR,
  // The vendor has settled but the bill has not been marked PAID yet.
  TransactionStatus.SUCCESS,
];

/**
 * A bill's payment is "in progress" when the tenant has paid but the payment vendor has not
 * settled it yet (e.g. EFT/PAD takes days, Interac waits for the tenant to send the e-Transfer),
 * so the bill is still PENDING.
 * - Stripe: an abandoned checkout is not in progress: it has no payment intent (`pi_`) yet and
 *   goes stale, and the server lets the tenant retry it.
 * - Interac (PCommerce) and other vendors: in progress until the vendor confirms or the payment
 *   fails, matching the server, which refuses a new payment for the bill until then. Requests the
 *   tenant never completes are failed by the server's daily transaction timeout job.
 */
export const isBillPaymentInProgress = (
  bill: Pick<IBill, 'status' | 'trxLogRef'>,
  trx?: Pick<ITransactionLog, 'source' | 'status' | 'timestamp' | 'referenceId'>
) => {
  if (bill.status !== BillStatus.PENDING || !bill.trxLogRef || !trx) return false;
  if (!IN_PROGRESS_TRX_STATUSES.includes(trx.status)) return false;
  if (trx.source !== Vendors.STRIPE) return true;

  const isPaymentSubmitted = !!trx.referenceId?.startsWith('pi_');
  return isPaymentSubmitted || Date.now() - trx.timestamp <= STALE_CHECKOUT_MS;
};
